import * as THREE from 'three';
import {
  FOCUS_ORBIT_DIST,
  NEURAL_ACCENT_HEX,
  NEURAL_ACCENT_LERP,
  PLANE_VIEW_TILT,
  STATUS_HEX,
} from '../constants';

export function isHexColor(s) {
  return typeof s === 'string' && /^#[0-9a-fA-F]{6}$/.test(s.trim());
}

/** Label border in the node's primary tag colour. */
export function tagStroke(color, alpha = 0.58) {
  if (!color || color.r == null) return `rgba(170, 200, 255, ${alpha})`;
  return `rgba(${(color.r * 255) | 0}, ${(color.g * 255) | 0}, ${(color.b * 255) | 0}, ${alpha})`;
}

export function neuralBaseColor(node) {
  const cat = node.statusCategory;
  const fallback = STATUS_HEX[cat] ?? STATUS_HEX.not_started;
  const hex = node.statusColorHex;
  if (isHexColor(hex)) {
    const c = new THREE.Color();
    c.setStyle(hex.trim());
    return c;
  }
  return new THREE.Color(fallback);
}

/** Same tint as the node mesh (status color + shared accent lerp). */
export function neuralDisplayColor(node) {
  const base = neuralBaseColor(node);
  const accent = new THREE.Color(NEURAL_ACCENT_HEX);
  return base.clone().lerp(accent, NEURAL_ACCENT_LERP);
}

const WIRE_GREY = new THREE.Color('#9aa3b8');

/** Hairline: one light grey. Nodes carry the colour. */
export function edgeStyleFromSource(node) {
  const cat = node.statusCategory;
  const opacityByCategory = {
    blocked: 0.26,
    completed: 0.12,
    in_progress: 0.28,
    not_started: 0.18,
  };
  return {
    color: WIRE_GREY,
    opacity: node.isExcluded ? 0.08 : opacityByCategory[cat] ?? opacityByCategory.not_started,
  };
}

/** Hub → nested-campaign entry tasks: same grey, slightly softer. */
export function edgeStyleForNest(hubNode) {
  return {
    color: WIRE_GREY,
    opacity: hubNode?.isExcluded ? 0.06 : 0.16,
  };
}

/** Nested leaf → hub: same grey, quieter than the stem. */
export function edgeStyleForReturn(leafNode) {
  return {
    color: WIRE_GREY,
    opacity: leafNode?.isExcluded ? 0.04 : 0.11,
  };
}

export function truncateLabel(str, max) {
  if (!str || str.length <= max) return str || 'Untitled';
  return `${str.slice(0, max - 1)}…`;
}

/** Mark a node whose rest pose just hopped islands so it glides, not jitters. */
export function markIslandJump(ud, from, to, dist2 = 0.4) {
  if (!to || !ud) return false;
  const origin = ud.homePos || from;
  if (!origin) return false;
  const dx = to.x - origin.x;
  const dy = to.y - origin.y;
  const dz = (to.z ?? 0) - (origin.z ?? 0);
  if (dx * dx + dy * dy + dz * dz <= dist2) return false;
  ud.revealX = 0;
  ud.revealY = 0;
  ud.titleClearing = false;
  ud.layoutJump = true;
  return true;
}

/** Identity, wires, island membership, and the aspect-aware layout key — not status or title edits. */
export function getGraphStructureKey(g) {
  const { nodes, edges } = g;
  if (!nodes?.length) return 'empty';
  const nodeKey = nodes.map((n) => n.id).join('\0');
  const edgeKey = edges
    .map(([a, b]) => `${nodes[a]?.id}>${nodes[b]?.id}`)
    .sort()
    .join('|');
  const islandKey = nodes.map((n) => n.clusterKey ?? '').join('\0');
  return `${nodeKey}#${edgeKey}#${islandKey}#${g.layoutKey ?? ''}`;
}

/** Camera direction for the plane view: straight on with a slight, cinematic tilt. */
export function planeViewDirection() {
  return new THREE.Vector3(0, PLANE_VIEW_TILT, 1).normalize();
}

/**
 * Eye position that looks at `targetWorld` on the graph plane from `orbitDist` away.
 * (Nothing can occlude a node in a 2D layout, so no sampling is needed.)
 */
export function computeBestCameraForNode(targetWorld, orbitDist = FOCUS_ORBIT_DIST) {
  return targetWorld.clone().addScaledVector(planeViewDirection(), orbitDist);
}

/** Canvas-fraction insets. A number is treated as the top inset (older callers). */
export function normalizeFrameInsets(insets) {
  if (insets == null) return { top: 0, left: 0, right: 0, bottom: 0 };
  if (typeof insets === 'number') return { top: insets, left: 0, right: 0, bottom: 0 };
  return {
    top: insets.top || 0,
    left: insets.left || 0,
    right: insets.right || 0,
    bottom: insets.bottom || 0,
  };
}

const NODE_CORE_R = 0.155;
const _unproj = new THREE.Vector3();
const _unprojDir = new THREE.Vector3();
const _unprojHit = new THREE.Vector3();
const _unprojRay = new THREE.Ray();
const _unprojPlane = new THREE.Plane();
const _screenRight = new THREE.Vector3();
const _screenEdge = new THREE.Vector3();
const _screenLook = new THREE.Vector3();
const _screenMid = new THREE.Vector3();

export function emptyFrameBounds() {
  return { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
}

export function expandFrameBounds(bounds, minX, maxX, minY, maxY) {
  bounds.minX = Math.min(bounds.minX, minX);
  bounds.maxX = Math.max(bounds.maxX, maxX);
  bounds.minY = Math.min(bounds.minY, minY);
  bounds.maxY = Math.max(bounds.maxY, maxY);
  return bounds;
}

export function finishFrameBounds(bounds) {
  if (!Number.isFinite(bounds.minX)) return { minX: -6, maxX: 6, minY: -4, maxY: 4 };
  return bounds;
}

/**
 * Disks only. Do not invent title/name air — that shoves the visual
 * center when a label sits on one side of an island.
 */
export function contentFrameBounds(items) {
  const bounds = emptyFrameBounds();
  (items || []).forEach((item) => {
    const x = item?.x ?? 0;
    const y = item?.y ?? 0;
    const r = NODE_CORE_R * (item?.size > 0 ? item.size : 1);
    expandFrameBounds(bounds, x - r, x + r, y - r, y + r);
  });
  return finishFrameBounds(bounds);
}

export function contentItemsFromGraph(nodes) {
  return (nodes || []).map((n) => ({
    x: n.pos?.[0] ?? 0,
    y: n.pos?.[1] ?? 0,
    size: n.size ?? 1,
  }));
}

function elPxSize(el) {
  const w = el?.offsetWidth;
  const h = el?.offsetHeight;
  if (w > 2 && h > 2) return { w, h };
  const chars = Math.min((el?.textContent || '').length, 28);
  return { w: Math.min(14 + chars * 6.1, 180), h: 18 };
}

/** Pixel box of a CSS2D tag (same origin rules as the label renderer). */
export function css2dScreenBox(ndcX, ndcY, el, center, viewW, viewH) {
  const { w, h } = elPxSize(el);
  const cx = center?.x ?? 0.5;
  const cy = center?.y ?? 0;
  const sx = (ndcX * 0.5 + 0.5) * viewW;
  const sy = (-ndcY * 0.5 + 0.5) * viewH;
  return { left: sx - w * cx, top: sy - h * cy, w, h };
}

/** Unproject a screen pixel onto the graph plane at `z`. */
export function unprojectScreenToPlane(camera, sx, sy, viewW, viewH, z = 0, out = _unprojHit) {
  const ndcX = (sx / viewW) * 2 - 1;
  const ndcY = 1 - (sy / viewH) * 2;
  _unproj.set(ndcX, ndcY, 0.5).unproject(camera);
  _unprojDir.copy(_unproj).sub(camera.position);
  if (_unprojDir.lengthSq() < 1e-12) return null;
  _unprojRay.set(camera.position, _unprojDir.normalize());
  _unprojPlane.set(new THREE.Vector3(0, 0, 1), -z);
  if (!_unprojRay.intersectPlane(_unprojPlane, out)) return null;
  return out;
}

/** World AABB of a screen-space label, so left/right air matches what you see. */
export function screenBoxToWorldBounds(camera, box, viewW, viewH, z = 0) {
  const corners = [
    [box.left, box.top],
    [box.left + box.w, box.top],
    [box.left, box.top + box.h],
    [box.left + box.w, box.top + box.h],
  ];
  const bounds = emptyFrameBounds();
  for (let i = 0; i < 4; i += 1) {
    const hit = unprojectScreenToPlane(camera, corners[i][0], corners[i][1], viewW, viewH, z);
    if (!hit) continue;
    expandFrameBounds(bounds, hit.x, hit.x, hit.y, hit.y);
  }
  return Number.isFinite(bounds.minX) ? bounds : null;
}

function expandScreenBox(box, left, top, w, h) {
  box.minX = Math.min(box.minX, left);
  box.maxX = Math.max(box.maxX, left + w);
  box.minY = Math.min(box.minY, top);
  box.maxY = Math.max(box.maxY, top + h);
  return box;
}

function labelIsHidden(el) {
  return el.style.visibility === 'hidden' || parseFloat(el.style.opacity) < 0.08;
}

function projectCss2dBox(camera, obj, el, viewW, viewH) {
  obj.getWorldPosition(_unproj);
  const z = _unproj.z;
  _unproj.project(camera);
  if (_unproj.z < -1 || _unproj.z > 1) return null;
  return { z, screen: css2dScreenBox(_unproj.x, _unproj.y, el, obj.center, viewW, viewH) };
}

function titleFallbackWorld(bunch, tag, key) {
  const ox = bunch.position.x + (bunch.userData.contentOffX ?? 0);
  const top = bunch.position.y
    + (bunch.userData.contentOffY ?? 0)
    + (bunch.userData.contentH ?? 0.6)
    + 0.4;
  const hw = Math.min(2.4, 0.4 + (tag.element.textContent || key).length * 0.06);
  return { minX: ox - hw, maxX: ox + hw, minY: top, maxY: top + 0.36 };
}

/**
 * Pixel union of disks, hanging names, and cluster titles.
 * This is the leftover-air spec — not the world AABB midpoint.
 */
export function gatherOverviewScreenExtents({
  camera,
  container,
  meshByTaskId,
  labelByTaskId,
  nebula,
}) {
  const viewW = Math.max(container?.clientWidth || 1, 1);
  const viewH = Math.max(container?.clientHeight || 1, 1);
  const box = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity, viewW, viewH };
  if (!camera) return null;

  camera.updateMatrixWorld(true);
  _screenRight.setFromMatrixColumn(camera.matrixWorld, 0).normalize();

  if (meshByTaskId) {
    meshByTaskId.forEach((mesh) => {
      const ud = mesh.userData;
      if (!mesh.visible || ud?.exiting || (ud?.visT ?? 1) < 0.05) return;
      const r = NODE_CORE_R * (ud.layoutScale > 0 ? ud.layoutScale : (ud.nodeMeta?.size ?? 1));
      _unproj.copy(mesh.position).project(camera);
      if (_unproj.z < -1 || _unproj.z > 1) return;
      const sx = (_unproj.x * 0.5 + 0.5) * viewW;
      const sy = (-_unproj.y * 0.5 + 0.5) * viewH;
      _screenEdge.copy(mesh.position).addScaledVector(_screenRight, r).project(camera);
      const pr = Math.max(Math.abs((_screenEdge.x * 0.5 + 0.5) * viewW - sx), 1);
      expandScreenBox(box, sx - pr, sy - pr, pr * 2, pr * 2);
    });
  }
  if (labelByTaskId) {
    labelByTaskId.forEach((rec) => {
      if (!rec?.label?.parent || !rec.element || labelIsHidden(rec.element)) return;
      const hit = projectCss2dBox(camera, rec.label, rec.element, viewW, viewH);
      if (hit) expandScreenBox(box, hit.screen.left, hit.screen.top, hit.screen.w, hit.screen.h);
    });
  }
  nebula?.children?.forEach((bunch) => {
    const tag = bunch.userData?.tag;
    const key = bunch.userData?.clusterKey;
    if (!tag?.element || !key || key === '__none__') return;
    const hit = projectCss2dBox(camera, tag, tag.element, viewW, viewH);
    if (hit) {
      expandScreenBox(box, hit.screen.left, hit.screen.top, hit.screen.w, hit.screen.h);
      return;
    }
    const fb = titleFallbackWorld(bunch, tag, key);
    [
      [fb.minX, fb.minY],
      [fb.maxX, fb.minY],
      [fb.minX, fb.maxY],
      [fb.maxX, fb.maxY],
    ].forEach(([x, y]) => {
      _unproj.set(x, y, 0).project(camera);
      if (_unproj.z < -1 || _unproj.z > 1) return;
      const sx = (_unproj.x * 0.5 + 0.5) * viewW;
      const sy = (-_unproj.y * 0.5 + 0.5) * viewH;
      expandScreenBox(box, sx, sy, 0, 0);
    });
  });
  return Number.isFinite(box.minX) ? box : null;
}

/**
 * Slide look-at X so leftover pixels left of content equal leftover pixels right.
 * Positive screen mid (content too far right) looks further right so the graph walks left.
 */
export function shiftTargetToEqualScreenAir(camera, target, screen) {
  if (!camera || !target || !screen || !Number.isFinite(screen.minX)) return target;
  const midX = (screen.minX + screen.maxX) * 0.5;
  const wantX = screen.viewW * 0.5;
  if (Math.abs(midX - wantX) < 0.35) return target;
  const look = unprojectScreenToPlane(
    camera,
    wantX,
    screen.viewH * 0.5,
    screen.viewW,
    screen.viewH,
    0,
    _screenLook,
  );
  const mid = unprojectScreenToPlane(
    camera,
    midX,
    screen.viewH * 0.5,
    screen.viewW,
    screen.viewH,
    0,
    _screenMid,
  );
  if (!look || !mid) return target;
  target.x += mid.x - look.x;
  return target;
}

/**
 * Tight box around visible disks, hanging names, and cluster titles.
 * Clouds are ignored. Zoom / orbit-contain use this; leftover air uses screen extents.
 */
export function gatherOverviewBounds({
  camera,
  container,
  meshByTaskId,
  labelByTaskId,
  nebula,
  fallback,
  taskIds = null,
  includeHiddenLabels = false,
}) {
  const bounds = emptyFrameBounds();
  const idSet = taskIds ? (taskIds instanceof Set ? taskIds : new Set(taskIds)) : null;
  const viewW = Math.max(container?.clientWidth || 1, 1);
  const viewH = Math.max(container?.clientHeight || 1, 1);
  const includeScreen = (obj, el, { ignoreHidden = false } = {}) => {
    if (!obj || !el || !camera) return false;
    if (!ignoreHidden && labelIsHidden(el)) return false;
    const hit = projectCss2dBox(camera, obj, el, viewW, viewH);
    if (!hit) return false;
    const world = screenBoxToWorldBounds(camera, hit.screen, viewW, viewH, hit.z);
    if (!world) return false;
    expandFrameBounds(bounds, world.minX, world.maxX, world.minY, world.maxY);
    return true;
  };

  if (meshByTaskId) {
    meshByTaskId.forEach((mesh, id) => {
      if (idSet && !idSet.has(id)) return;
      const ud = mesh.userData;
      if (!mesh.visible || ud?.exiting || (ud?.visT ?? 1) < 0.05) return;
      const r = NODE_CORE_R * (ud.layoutScale > 0 ? ud.layoutScale : (ud.nodeMeta?.size ?? 1));
      expandFrameBounds(bounds, mesh.position.x - r, mesh.position.x + r, mesh.position.y - r, mesh.position.y + r);
    });
  }
  if (labelByTaskId) {
    labelByTaskId.forEach((rec, id) => {
      if (idSet && !idSet.has(id)) return;
      if (!rec?.label?.parent) return;
      includeScreen(rec.label, rec.element, { ignoreHidden: includeHiddenLabels });
    });
  }
  if (!idSet) {
    nebula?.children?.forEach((bunch) => {
      const tag = bunch.userData?.tag;
      const key = bunch.userData?.clusterKey;
      if (!tag?.element || !key || key === '__none__') return;
      if (includeScreen(tag, tag.element, { ignoreHidden: true })) return;
      const fb = titleFallbackWorld(bunch, tag, key);
      expandFrameBounds(bounds, fb.minX, fb.maxX, fb.minY, fb.maxY);
    });
  }
  if (!Number.isFinite(bounds.minX)) return fallback || finishFrameBounds(bounds);
  return bounds;
}

/**
 * Distance needed for a perspective camera to frame a world-space box.
 * Insets reserve that share of the FOV (toolbar / fade). Left cards are
 * overlays — pass only a whisper of left, never a full column crop.
 * @param {{minX:number,maxX:number,minY:number,maxY:number}} bounds
 */
export function distanceToFrameBounds(bounds, fovDeg, aspect, padding = 1.04, insets = 0) {
  const { top, left, right, bottom } = normalizeFrameInsets(insets);
  const halfW = Math.max(2, ((bounds?.maxX ?? 6) - (bounds?.minX ?? -6)) / 2) * padding;
  const halfH = Math.max(2, ((bounds?.maxY ?? 4) - (bounds?.minY ?? -4)) / 2) * padding;
  const tanV = Math.tan(THREE.MathUtils.degToRad(fovDeg / 2));
  const tanH = tanV * Math.max(aspect, 0.2);
  const usableTanV = tanV * Math.max(1 - top - bottom, 0.5);
  const usableTanH = tanH * Math.max(1 - left - right, 0.5);
  return Math.max(halfH / usableTanV, halfW / usableTanH);
}

/**
 * Vertical look-at shift for the top fade / campaign HUD.
 * Do not shift X: left mission cards float over the canvas, they do not
 * remove a column. A left shift makes the graph look parked on the right.
 */
export function frameLookShift(dist, fovDeg, aspect, insets = 0) {
  const { top, bottom } = normalizeFrameInsets(insets);
  const viewWorldH = 2 * dist * Math.tan(THREE.MathUtils.degToRad(fovDeg / 2));
  return {
    x: 0,
    y: viewWorldH * (top - bottom) * 0.5,
  };
}

function overlayLeftPx(container) {
  const w = Math.max(container?.clientWidth || 1, 1);
  const root = container?.parentElement;
  const cr = container?.getBoundingClientRect?.();
  if (!root || !cr) return 0;
  let leftPx = 0;
  const cards = root.querySelectorAll('.neural-view__mission-hud .flowchart-hud__glass-card');
  cards.forEach((el) => {
    const r = el.getBoundingClientRect?.();
    if (!r || r.width < 8 || r.height < 8) return;
    if (r.left < cr.left + w * 0.55) {
      leftPx = Math.max(leftPx, r.right - cr.left);
    }
  });
  return leftPx > 0 ? leftPx + 16 : 0;
}

/** Top fade / campaign HUD plus left mission cards, as canvas fractions. */
export function overviewViewInsets(container) {
  const w = Math.max(container?.clientWidth || 1, 1);
  const h = Math.max(container?.clientHeight || 1, 1);
  const root = container?.parentElement;
  const hud = root?.querySelector('.neural-view__hud');
  const fade = root?.querySelector('.neural-view__top-gradient');
  const hudH = hud?.getBoundingClientRect().height ?? 0;
  const fadeH = fade?.getBoundingClientRect().height ?? 80;
  const topPx = Math.max(hudH + 8, fadeH + 6, 72);
  return {
    top: Math.min(topPx / h, 0.26),
    left: Math.min(overlayLeftPx(container) / w, 0.42),
    right: 0,
    bottom: 0,
  };
}

/**
 * What the overview camera actually uses. Top fade is a real crop.
 * Left cards only add a little zoom-out so the right edge is not flush.
 */
export function overviewFrameInsets(container) {
  const raw = overviewViewInsets(container);
  return {
    top: raw.top,
    left: 0,
    right: 0,
    bottom: 0,
  };
}

/** How much of the canvas height sits under the campaign HUD + top fade. */
export function overviewTopInsetFrac(container) {
  return overviewViewInsets(container).top;
}
